import { createRouter as _createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router';

import { BaseApplicationOptions } from '../types/Application';
import {
    ApplicationFactory,
    ExtractModuleDemandedOptions,
    FinalApplication,
    PartialModuleDeclaration,
} from '../types/ModuleDeclaration';

import { Application } from './Application';
import { SealedModule } from './SealedModule';

export class ApplicationBuilder<T extends PartialModuleDeclaration = {}> {
    private modules: SealedModule[] = [];
    private readonly mainComponent: any;

    public constructor(mainComponent: any) {
        this.mainComponent = mainComponent;
    }

    // TODO: Improve type definitions, so it will show error about missing demands
    // Make sure that it will be performant - earlier implementation failed,
    // so autocomplete from inferred type wasn't working
    public use<D extends PartialModuleDeclaration>(appModule: SealedModule<D>): ApplicationBuilder<T & D> {
        this.modules.push(appModule);
        // @ts-ignore: simulating change
        return this;
    }

    public createFactory(): ApplicationFactory<T> {
        // Copy list of modules, so it will be sealed
        const modules = [ ...this.modules ];
        const mainComponent = this.mainComponent;

        return async (_options: BaseApplicationOptions & ExtractModuleDemandedOptions<T>) => {
            // Build final options
            const options = {
                ..._options,
                // PRODUCT-26110 Phase 2B2: vue-router 3's `Router` was a class
                // (`new options.Router({...})`); vue-router 4 has no class to `new` at
                // all — `createRouter` is a factory function. `options.Router` is
                // threaded through `BaseApplicationOptions` (types/Application.ts) as
                // `typeof createRouter`, i.e. a *callable factory*, not a constructor,
                // so this default and every caller of `createRouter` below drop `new`.
                Router: _options.Router || _createRouter,
                // PRODUCT-26110 Phase 2B2 Task 1 (Quasar 2): defaulted the same way
                // as Vue/Vuex/Router above, so `Required<BaseApplicationOptions>`
                // (Application's stored `options` type) is always satisfied even
                // when a caller (e.g. ido-facing) never passes `appPlugins` at all.
                appPlugins: _options.appPlugins || [],
            };

            const initialize = (app: any) => modules.reduce(
                (promise, next) => promise.then(() => next.registerInjections(app)),
                Promise.resolve(),
            );

            const createVueContext = (app: any) => modules.reduce((result, appModule) => ({
                ...result,
                ...appModule.buildVueInjections(app),
            }), {});

            // PRODUCT-26110 Phase 2B2: `mode:` was never set anywhere across the
            // repo's router construction, and vue-router 3 defaults `mode` to 'hash'
            // when unset. Confirmed by
            // src/vue-commons/compatibility/handleLegacyMithrilLocation.ts, which
            // exists specifically to rewrite legacy `#!/...` hash URLs into
            // vue-router's hash format. So createWebHashHistory() matches current
            // (and required) behavior, not createWebHistory().
            const createRouter = (app: any) => options.Router({
                history: createWebHashHistory(),
                routes: modules.reduce((result, appModule) => [
                    ...result,
                    ...appModule.buildRouteConfig(app),
                ], [] as RouteRecordRaw[]),
            });

            const buildSlotComponents = () => modules.reduce((result, appModule) => {
                const components = appModule.getSlotComponents();
                for (const slot of Object.keys(components)) {
                    if (!result[slot]) {
                        result[slot] = [];
                    }
                    result[slot] = [ ...result[slot], ...components[slot] ];
                }
                return result;
            }, {} as Record<string, any[]>);

            const buildProceduresMap = () => modules.reduce((result, appModule) => ({
                ...result,
                ...appModule.getProceduresMap(),
            }), {} as Record<string, any>);

            const app = new Application(options, createVueContext, mainComponent) as any as FinalApplication<T>;

            // Initialize
            // @ts-ignore: it's hidden property
            await app.initialize({ initialize, createRouter, buildSlotComponents, buildProceduresMap });

            // Execute all set-up functions
            for (const appModule of modules) {
                appModule.execute(app as any);
            }

            return app;
        };
    }
}
